import { isEmpty } from '@fruitful/fruitils';
import {
    AnalyticsUserEvent,
    MoneyMapAuthorType,
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router } from 'expo-router';
import React, { useEffect } from 'react';
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated';
import {
    Animations,
    BadgeLabel,
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    GuideBlurb,
    H5Emphasized,
    isWeb,
    MemberTip,
    MoneyMapIcon,
    Shadows,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';

import MoneyMapHomeVisual from '../../../assets/images/money-map/money-map-home-visual.svg';
import MoneyMapVisual from '../../../assets/images/money-map/money-map-visual.svg';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../common/utils/homeUtils';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { useMoneyMapProjectionDisplay } from '../../home/components/goal-tile/MoneyMapProjectionGraphic';
import { useGetMembershipMastraThread } from '../../llm-service/hooks/useGetMembershipMastraThread';
import { track } from '../../logger/Logger';
import { useUpdateMemberJourneyLog } from '../../member-profile/hooks/useUpdateMemberJourneyLog';
import { useGetMoneyMapForMembership } from '../hooks/useGetMoneyMapsForMembership';
import { useMoneyMapWorkflow } from '../hooks/useMoneyMapWorkflow';
import {
    MoneyMapEarningsTileForDirectDeposit,
    MoneyMapEarningsTileForMoneyMap,
} from './MoneyMapEarnings';

type MoneyMapTileType =
    | 'preview'
    | 'setup'
    | 'completed'
    | 'loading'
    | 'empty'
    | 'error';

const MM_DESCRIPTION_LONG =
    'A supportive money system that keeps you on track effortlessly. Spend from your designated spending account while bills and savings are handled automatically.';

export const RoadmapMoneyMapSection = () => {
    // useGetMoneyMapForMembership fetches both PREVIEW and ACTIVE maps — needed
    // here so the tile can detect PREVIEW status. useMoneyMapWorkflow (which
    // only fetches ACTIVE maps internally) provides Go Flow completeness state.
    const { data: moneyMap, isLoading: isMoneyMapLoading } =
        useGetMoneyMapForMembership();
    const { hasMoneyMapError, isWorkflowCompleted, isLoadingCompleteness } =
        useMoneyMapWorkflow();

    const status = moneyMap?.status;
    let type: MoneyMapTileType = 'empty';

    if (isMoneyMapLoading || isLoadingCompleteness) {
        type = 'loading';
    } else if (hasMoneyMapError) {
        type = 'error';
    } else if (status === MoneyMapStatus.PREVIEW) {
        type = 'preview';
    } else if (status === MoneyMapStatus.ACTIVE && !isWorkflowCompleted) {
        type = 'setup';
    } else if (status === MoneyMapStatus.ACTIVE && isWorkflowCompleted) {
        type = 'completed';
    }

    return (
        <>
            {type === 'completed' && (
                <MoneyMapEarningsTileForDirectDeposit
                    variant="dual-action"
                    moneyMap={moneyMap}
                    isMoneyMapCompleted={true}
                    appContext="roadmap-tab-money-map-tile"
                />
            )}
            <MoneyMapTile type={type} context="roadmap" moneyMap={moneyMap} />
        </>
    );
};

export const HomeMoneyMapSection = () => {
    const user = useAuthedUser();
    const { updateMemberJourneyLog } = useUpdateMemberJourneyLog();
    const { data: moneyMap, isLoading: isMoneyMapLoading } =
        useGetMoneyMapForMembership();
    const {
        isWorkflowCompleted,
        isLoadingMoneyMap,
        isLoadingCompleteness,
        moneyMapCompleteness,
    } = useMoneyMapWorkflow();

    // When GOYO is on and the projection card (with the graph) is visible, it
    // replaces this money map setup section — hide this one to avoid both.
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { data: mastraThread } = useGetMembershipMastraThread({
        membershipId: user.membership?.id,
    });
    const projectionDisplay = useMoneyMapProjectionDisplay(mastraThread);
    // A guide-authored money map (e.g. a guide duplicated/edited a CMM map)
    // exits the GOYO/robo experience, so this classic "Your Money Map is here! /
    // Review your Money Map" section should show instead of the GOYO projection.
    const isGuideAuthoredMoneyMap =
        moneyMap?.createdByType === MoneyMapAuthorType.GUIDE;

    // Under GOYO the home money-map widget is the projection card (pre-activation)
    // and then the BankingSwitch "Put your Money Map into action" widget — this
    // "Your Money Map is here! / Review your Money Map" section is never part of
    // that flow, so keep it hidden whenever the member has a GOYO projection
    // (unless a guide has taken the map over, per above).
    const projectionCardVisible =
        goyoEnabled && projectionDisplay != null && !isGuideAuthoredMoneyMap;

    useEffect(() => {
        if (isWorkflowCompleted && !user.memberJourneyLog?.completedMoneyMap) {
            void updateMemberJourneyLog({ completedMoneyMap: true });
        }
    }, [
        isWorkflowCompleted,
        user.memberJourneyLog?.completedMoneyMap,
        updateMemberJourneyLog,
    ]);

    // Loading state
    if (isMoneyMapLoading || isLoadingMoneyMap || isLoadingCompleteness) {
        return null;
    }

    // The GOYO projection card is showing the graph instead — hide this section.
    if (projectionCardVisible) {
        return null;
    }

    if (
        moneyMap?.status === MoneyMapStatus.PREVIEW ||
        (moneyMap?.status === MoneyMapStatus.ACTIVE && !isWorkflowCompleted)
    ) {
        const guideImageSrc = user.membership?.guide?.profileImageUrl;

        const { reviewMoneyMapStep, incomeSplitStep } = moneyMapCompleteness;
        const hasPartner = user.partnerMember != null;
        const shouldShowIncomeSplitTip =
            hasPartner &&
            incomeSplitStep.isIncluded &&
            !incomeSplitStep.incomeSplitCreated &&
            !reviewMoneyMapStep.isCompleteForMembership;

        const incomeSplitTipMessage =
            'You and your partner are both required to review and complete the Money Map setup before you can turn on your Income Split.';

        return (
            <Animated.View
                style={{ width: '100%' }}
                entering={FadeIn.duration(500)}
                {...(!isWeb && { layout: LinearTransition.duration(500) })}
            >
                <YStack
                    gap={24}
                    width="100%"
                    maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
                    alignSelf="center"
                >
                    <YStack gap={12}>
                        <GuideBlurb
                            guideImageSrc={guideImageSrc}
                            heading="Your Money Map is here!"
                            message="As part of your Money Map setup, we'll guide you through opening necessary Fruitful accounts and turning on your Income Split."
                        />
                        <MoneyMapTile
                            type={
                                moneyMap?.status === MoneyMapStatus.PREVIEW
                                    ? 'preview'
                                    : 'setup'
                            }
                            context="home"
                            moneyMap={moneyMap}
                        />
                    </YStack>
                    {shouldShowIncomeSplitTip && (
                        <MemberTip
                            icon={MoneyMapIcon}
                            text={incomeSplitTipMessage}
                        />
                    )}
                </YStack>
            </Animated.View>
        );
    }

    return null;
};

const MoneyMapTile = ({
    type,
    context,
    moneyMap,
}: {
    type: MoneyMapTileType;
    context: 'home' | 'roadmap';
    moneyMap?: MoneyMapWithRulesAndAccounts;
}) => {
    const eventData = {
        appContext: context,
    };

    let title: string;
    let buttonLabel: string | undefined;
    let onPress: (() => void) | undefined;

    switch (type) {
        case 'loading':
            title = '';
            break;
        case 'error':
            title = 'Fruitful Money Map';
            break;
        case 'preview':
            title = 'Your Money Map is here!';
            buttonLabel = 'Explore your Money Map';
            onPress = () => {
                track(AnalyticsUserEvent.OPEN_PREVIEW_MONEY_MAP, eventData);
                router.navigate('/roadmap/money-map');
            };
            break;
        case 'setup':
            title = 'Your Money Map is here!';
            buttonLabel = 'Set up your Money Map';
            onPress = () => {
                track(AnalyticsUserEvent.OPEN_MONEY_MAP_WORKFLOW, eventData);
                router.navigate('/roadmap/go/start');
            };
            break;
        case 'completed':
            title = 'Your Money Map';
            buttonLabel = 'Explore your Money Map';
            onPress = () => {
                track(AnalyticsUserEvent.OPEN_COMPLETED_MONEY_MAP, eventData);
                router.navigate('/roadmap/money-map');
            };
            break;
        case 'empty':
        default:
            title = 'Fruitful Money Map';
            buttonLabel = 'Ask your Guide about this';
            onPress = () => {
                track(
                    AnalyticsUserEvent.OPEN_MONEY_MAP_GUIDE_PROMPT,
                    eventData,
                );
                router.navigate('/home/my-guide');
            };
    }

    return context === 'roadmap' ? (
        <MegaMoneyMapTile
            moneyMap={moneyMap}
            isLoading={type === 'loading'}
            isError={type === 'error'}
            isMoneyMapCompleted={type === 'completed'}
            title={title}
            buttonLabel={buttonLabel}
            onPress={onPress}
        />
    ) : context === 'home' ? (
        <HomeMoneyMapTile onPress={onPress ?? (() => {})} />
    ) : null;
};

const MegaMoneyMapTile = ({
    isLoading = false,
    isError = false,
    title = '',
    buttonLabel,
    onPress,
    moneyMap,
    isMoneyMapCompleted,
}: {
    isLoading?: boolean;
    isError?: boolean;
    title?: string;
    buttonLabel?: string;
    onPress?: () => void;
    moneyMap?: MoneyMapWithRulesAndAccounts;
    isMoneyMapCompleted?: boolean;
}) => {
    return (
        <YStack
            width="100%"
            maxWidth={500}
            borderRadius={24}
            borderWidth={1}
            borderColor="$gray400"
            backgroundColor="$white"
            padding={16}
            paddingTop={20}
            gap={16}
            justifyContent="space-between"
            alignItems="center"
            {...Shadows.SMALL}
            {...Animations.FadeInFromAbove}
            $sm={{ width: 500 }}
        >
            {isLoading ? (
                <Spinner height={50} {...Animations.FadeIn} />
            ) : (
                <YStack gap={8}>
                    <H5Emphasized>{title}</H5Emphasized>
                    {!isError && (
                        <BodyMediumRegular>
                            {MM_DESCRIPTION_LONG}
                        </BodyMediumRegular>
                    )}
                </YStack>
            )}
            <MoneyMapVisual />
            {
                isLoading ? null : isError ? (
                    <XStack
                        flex={1}
                        backgroundColor="$gray100"
                        justifyContent="center"
                        padding={12}
                        borderRadius={12}
                    >
                        <BodySmallRegular>
                            Money Map could not be loaded
                        </BodySmallRegular>
                    </XStack>
                ) : !isEmpty(buttonLabel) && onPress ? (
                    <View width="100%" {...Animations.FadeIn}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text={buttonLabel ?? ''}
                            onPress={onPress}
                        />
                    </View>
                ) : null // Should not be possible to get here
            }
            {moneyMap && (
                <MoneyMapEarningsTileForMoneyMap
                    moneyMap={moneyMap}
                    isMoneyMapCompleted={!!isMoneyMapCompleted}
                    appContext="roadmap-tab-money-map-tile"
                />
            )}
        </YStack>
    );
};

const HomeMoneyMapTile = ({ onPress }: { onPress: () => void }) => {
    return (
        <YStack
            width="100%"
            maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
            backgroundColor="$gray200"
            borderRadius={24}
            padding={24}
            gap={24}
        >
            <XStack justifyContent="space-between" alignItems="center">
                <BodyLargeEmphasized>Your Money Map</BodyLargeEmphasized>
                <BadgeLabel variant="positive" text="Ready" />
            </XStack>
            <YStack gap={16}>
                <BodySmallRegular color="$gray1100" textAlign="center">
                    Your money, organized. Spend from your designated spending
                    account while bills and savings are handled automatically.
                </BodySmallRegular>
                <View alignSelf="center">
                    <MoneyMapHomeVisual />
                </View>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Review your Money Map"
                    onPress={onPress}
                />
            </YStack>
        </YStack>
    );
};
